import { KeyboardKey, KeyboardShortcut, normalizeKeyboardShortcut } from '../../utils/keyboardShortcuts'
import { getOperatingSystem } from '../../utils/operatingSystem'
import { astraTwMerge } from '../Utilities/astraTwMerge'

type Variant = 'default' | 'inverted'

type Props = {
    shortcut: KeyboardShortcut
    variant?: Variant
}

const variantStyles: Record<Variant, string> = {
    default: 'bg-neutral-rest border border-muted text-muted',
    inverted: 'bg-surface-base/15 border border-transparent text-inverted/80',
}

const SHIFT_SYMBOL = '⇧'

const getMetaSymbol = () => (getOperatingSystem() === 'Mac' ? '⌘' : 'Ctrl')

// Keys whose name differs from what the key cap shows. Mac key caps show Return as a symbol, others write Enter out.
const getKeyLabel = (key: KeyboardKey) => {
    if (key === 'ENTER') {
        return getOperatingSystem() === 'Mac' ? '↩' : 'Enter'
    }
    return key
}

export const KeyboardShortcutPreview = ({ shortcut, variant = 'default' }: Props) => {
    const keys: string[] = []
    if (shortcut.hasShift) {
        keys.push(SHIFT_SYMBOL)
    }
    if (shortcut.hasMeta) {
        keys.push(getMetaSymbol())
    }
    keys.push(getKeyLabel(normalizeKeyboardShortcut(shortcut.keyboardKey)))

    return (
        <span
            className={astraTwMerge(
                `inline-flex w-fit min-w-5 items-center justify-center gap-px rounded px-0.5 text-center text-xs font-medium ${variantStyles[variant]}`
            )}
        >
            {keys.map(key => (
                // Wrapped so the flex gap applies between keys instead of collapsing them into one text run. The font sets
                // the characters a little high in their key cap, so they move down half a pixel to sit centred.
                <span key={key} className="translate-y-[0.5px]">
                    {key}
                </span>
            ))}
        </span>
    )
}
